import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAlbumById, updateAlbum, deleteAlbum, type AlbumData } from '@/services/albumService';
import { deletePhotosBatch, getPhotosByAlbumId } from '@/services/photoService';
import type { PhotoDetail } from '@/types/photo';
import { MultiDownloadButton } from '@/components/photo/PhotoDownloadButton';

export default function AlbumDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [album, setAlbum] = useState<AlbumData | null>(null);
  const [albumPhotos, setAlbumPhotos] = useState<PhotoDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Album cover picker
  const [showCoverPicker, setShowCoverPicker] = useState(false);
  const [selectedCoverUrl, setSelectedCoverUrl] = useState('');
  const [savingCover, setSavingCover] = useState(false);
  const [coverError, setCoverError] = useState('');

  // Selection mode
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showBatchDeleteConfirm, setShowBatchDeleteConfirm] = useState(false);
  const [batchDeleting, setBatchDeleting] = useState(false);
  const [batchDeleteError, setBatchDeleteError] = useState('');

  useEffect(() => {
    if (!id) {
      navigate('/albums');
      return;
    }

    const load = async () => {
      try {
        const [found, photos] = await Promise.all([
          getAlbumById(id),
          getPhotosByAlbumId(id),
        ]);
        if (!found) return;
        setAlbum(found);
        setAlbumPhotos(photos.sort((a, b) => b.shotDate.localeCompare(a.shotDate)));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, navigate]);

  const handleDelete = async () => {
    if (!id || deleting) return;
    setDeleting(true);
    try {
      await deleteAlbum(id);
      navigate('/albums');
    } finally {
      setDeleting(false);
    }
  };


  const openCoverPicker = () => {
    if (albumPhotos.length === 0) return;
    setSelectedCoverUrl(album?.coverImage ?? albumPhotos[0].image);
    setCoverError('');
    setShowCoverPicker(true);
  };

  const closeCoverPicker = () => {
    if (savingCover) return;
    setShowCoverPicker(false);
    setCoverError('');
  };

  const handleSaveCover = async () => {
    if (!id || !selectedCoverUrl || savingCover) return;

    setSavingCover(true);
    setCoverError('');
    try {
      const updated = await updateAlbum(id, { coverImage: selectedCoverUrl });
      if (!updated) {
        setCoverError('대표사진을 저장하지 못했습니다. 다시 시도해 주세요.');
        return;
      }
      setAlbum(updated);
      setShowCoverPicker(false);
    } catch {
      setCoverError('대표사진을 저장하지 못했습니다. 다시 시도해 주세요.');
    } finally {
      setSavingCover(false);
    }
  };

  const handlePhotoClick = (photo: PhotoDetail) => {
    if (selectionMode) {
      toggleSelect(photo.id);
    } else {
      navigate(`/albums/${id}/photos/${photo.id}`);
    }
  };

  const toggleSelect = (photoId: string) => {
    setSelectedIds((prev) =>
      prev.includes(photoId)
        ? prev.filter((pid) => pid !== photoId)
        : [...prev, photoId],
    );
  };

  const selectAll = () => {
    setSelectedIds(albumPhotos.map((p) => p.id));
  };

  const deselectAll = () => {
    setSelectedIds([]);
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds([]);
    setBatchDeleteError('');
  };

  const handleBatchDelete = async () => {
    if (!id || selectedIds.length === 0 || batchDeleting) return;
    setBatchDeleting(true);
    setBatchDeleteError('');
    try {
      await deletePhotosBatch(selectedIds);
      const [updatedAlbum, photos] = await Promise.all([
        getAlbumById(id),
        getPhotosByAlbumId(id),
      ]);
      if (updatedAlbum) setAlbum(updatedAlbum);
      setAlbumPhotos(photos.sort((a, b) => b.shotDate.localeCompare(a.shotDate)));
      setShowBatchDeleteConfirm(false);
      exitSelectionMode();
    } catch (deleteError) {
      setBatchDeleteError(
        deleteError instanceof Error ? deleteError.message : '선택한 사진을 삭제하지 못했습니다.',
      );
    } finally {
      setBatchDeleting(false);
    }
  };

  const totalSelectedSize = albumPhotos
    .filter((p) => selectedIds.includes(p.id))
    .reduce((sum, p) => sum + p.fileSize, 0);

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <header className="sticky top-0 z-20 bg-background-50/95 backdrop-blur-sm border-b border-background-200/50">
          <div className="flex items-center gap-3 px-4 py-3">
            <button
              onClick={() => navigate('/albums')}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-secondary-100 transition-colors cursor-pointer flex-shrink-0"
              aria-label="뒤로 가기"
            >
              <i className="ri-arrow-left-line text-xl text-foreground-700"></i>
            </button>
            <h1 className="text-base font-bold text-foreground-950 truncate">불러오는 중...</h1>
          </div>
        </header>
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-accent-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  // Album not found
  if (!album) {
    return (
      <div className="flex flex-col min-h-screen">
        <header className="sticky top-0 z-20 bg-background-50/95 backdrop-blur-sm border-b border-background-200/50">
          <div className="flex items-center gap-3 px-4 py-3">
            <button
              onClick={() => navigate('/albums')}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-secondary-100 transition-colors cursor-pointer flex-shrink-0"
              aria-label="뒤로 가기"
            >
              <i className="ri-arrow-left-line text-xl text-foreground-700"></i>
            </button>
            <h1 className="text-base font-bold text-foreground-950 truncate">앨범을 찾을 수 없어요</h1>
          </div>
        </header>
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-secondary-100 mb-4">
            <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-600 text-sm">앨범을 찾을 수 없어요</p>
          <button
            onClick={() => navigate('/albums')}
            className="mt-4 px-5 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            앨범 목록으로
          </button>
        </div>
      </div>
    );
  }

  const sortedPhotos = albumPhotos;

  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-background-50/95 backdrop-blur-sm border-b border-background-200/50">
        <div className="flex items-center gap-3 px-4 py-3">
          <button
            onClick={() => navigate('/albums')}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-secondary-100 transition-colors cursor-pointer flex-shrink-0"
            aria-label="뒤로 가기"
          >
            <i className="ri-arrow-left-line text-xl text-foreground-700"></i>
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold text-foreground-950 truncate">{album.title}</h1>
          </div>

          {!selectionMode && (
            <>
              <button
                onClick={() => navigate(`/album/${id}/edit`)}
                className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-secondary-100 transition-colors cursor-pointer flex-shrink-0"
                aria-label="앨범 수정"
              >
                <i className="ri-edit-line text-lg text-foreground-600"></i>
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0"
                aria-label="앨범 삭제"
              >
                <i className="ri-delete-bin-line text-lg text-foreground-500 hover:text-red-500"></i>
              </button>
            </>
          )}
        </div>
      </header>

      {/* Selection Mode Bar */}
      {selectionMode && (
        <div className="sticky top-[53px] z-20 bg-background-50/95 backdrop-blur-sm border-b border-background-200/50 px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={exitSelectionMode}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-secondary-100 transition-colors cursor-pointer"
              aria-label="선택 모드 종료"
            >
              <i className="ri-close-line text-lg text-foreground-600"></i>
            </button>
            <span className="text-sm font-medium text-foreground-800">
              {selectedIds.length > 0 ? `${selectedIds.length}장 선택됨` : '사진 선택'}
            </span>
          </div>
          <button
            onClick={selectedIds.length === albumPhotos.length ? deselectAll : selectAll}
            className="text-xs text-accent-500 font-medium hover:underline cursor-pointer whitespace-nowrap"
          >
            {selectedIds.length === albumPhotos.length ? '전체 해제' : '전체 선택'}
          </button>
        </div>
      )}

      {/* Album Info */}
      {!selectionMode && (
        <section className="px-4 pt-5 pb-3">
          <div className="relative w-full aspect-[16/9] rounded-xl overflow-hidden bg-secondary-100 mb-3 group">
            <img
              src={album.coverImage}
              alt={album.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/55 to-transparent pointer-events-none"></div>
            <button
              type="button"
              onClick={openCoverPicker}
              disabled={albumPhotos.length === 0}
              className="absolute right-3 bottom-3 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-background-50/95 text-foreground-800 text-xs font-semibold shadow-md hover:bg-background-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              aria-label="앨범 대표사진 변경"
            >
              <i className="ri-image-edit-line text-base"></i>
              대표사진 변경
            </button>
          </div>
          {albumPhotos.length === 0 && (
            <p className="text-[11px] text-foreground-400 mb-3">사진을 업로드하면 대표사진을 선택할 수 있어요.</p>
          )}
          {album.description && (
            <p className="text-sm text-foreground-600 leading-relaxed mb-3">{album.description}</p>
          )}
          {albumPhotos.length > 0 && (
            <button
              type="button"
              onClick={() => navigate(`/slideshow?albumId=${id}`)}
              className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-foreground-900 px-3.5 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-accent-500"
            >
              <i className="ri-slideshow-3-line text-base" />
              이 앨범 슬라이드쇼
            </button>
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-image-line text-sm"></i>
              </div>
              {album.photoCount}장의 사진
            </span>
            <span className="flex items-center gap-1">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-line text-sm"></i>
              </div>
              생성: {album.createdAt}
            </span>
            {album.updatedAt !== album.createdAt && (
              <span className="flex items-center gap-1">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-history-line text-sm"></i>
                </div>
                수정: {album.updatedAt}
              </span>
            )}
          </div>
        </section>
      )}

      {/* Selection Mode Toggle + Photo count */}
      <div className="px-4 pt-3 pb-2 flex items-center justify-between">
        <h2 className="text-sm font-bold text-foreground-800">
          사진 <span className="text-foreground-400 font-normal text-xs">({sortedPhotos.length}장)</span>
        </h2>
        {!selectionMode && sortedPhotos.length > 0 && (
          <button
            onClick={() => setSelectionMode(true)}
            className="text-xs text-foreground-500 hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
          >
            <i className="ri-checkbox-multiple-line text-sm"></i>
            선택
          </button>
        )}
      </div>

      {/* Divider */}
      <div className="px-4">
        <div className="border-t border-background-200/70"></div>
      </div>

      {/* Photo Grid */}
      <section className={`px-3 py-3 ${selectionMode ? 'pb-40' : 'pb-24'}`}>
        {sortedPhotos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-secondary-100 mb-3">
              <i className="ri-image-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500 text-sm">아직 등록된 사진이 없습니다</p>
            <p className="text-foreground-400 text-xs mt-1">첫 번째 사진을 올려보세요</p>
            <button
              onClick={() => navigate('/upload')}
              className="mt-4 px-5 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              사진 올리기
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {sortedPhotos.map((photo: PhotoDetail) => {
              const isSelected = selectedIds.includes(photo.id);
              return (
                <div
                  key={photo.id}
                  className="group cursor-pointer"
                  onClick={() => handlePhotoClick(photo)}
                >
                  <div className="relative aspect-square rounded-lg overflow-hidden bg-secondary-100">
                    <img
                      src={photo.image}
                      alt={photo.caption}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                    {photo.mediaType === 'video' && (
                      <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/10">
                        <i className="ri-play-circle-fill text-4xl text-white drop-shadow-lg" />
                      </div>
                    )}
                    {/* Selection checkbox */}
                    {selectionMode && (
                      <div className={`absolute top-2 left-2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                        isSelected
                          ? 'bg-accent-500 border-accent-500'
                          : 'bg-background-50/80 border-background-300'
                      }`}>
                        {isSelected && (
                          <i className="ri-check-line text-background-50 text-xs"></i>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="px-0.5 mt-1.5 mb-1">
                    <p className="text-[11px] font-medium text-foreground-800 truncate">{photo.caption}</p>
                    <p className="text-[10px] text-foreground-500">{photo.shotDate}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Selection mode bottom bar */}
      {selectionMode && (
        <div className="fixed bottom-[72px] left-0 right-0 z-[60] bg-background-50 border-t border-background-200/70 px-4 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
          {batchDeleteError && (
            <div className="mb-2 rounded-lg bg-red-50 px-3 py-2 text-center text-xs text-red-600">
              {batchDeleteError}
            </div>
          )}
          <div className="grid grid-cols-[minmax(0,1fr)_88px] gap-2">
            <MultiDownloadButton
              selectedIds={selectedIds}
              totalSize={totalSelectedSize}
              albumName={album.title}
              onComplete={exitSelectionMode}
            />
            <button
              type="button"
              onClick={() => {
                setBatchDeleteError('');
                setShowBatchDeleteConfirm(true);
              }}
              disabled={selectedIds.length === 0 || batchDeleting}
              className="flex h-[50px] items-center justify-center gap-1 rounded-xl bg-red-500 px-3 text-sm font-bold text-white transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <i className="ri-delete-bin-line text-base"></i>
              삭제
            </button>
          </div>
        </div>
      )}


      {/* Selected Photos Delete Confirmation */}
      {showBatchDeleteConfirm && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center px-6">
          <button
            type="button"
            className="absolute inset-0 bg-black/55 cursor-default"
            onClick={() => { if (!batchDeleting) setShowBatchDeleteConfirm(false); }}
            aria-label="선택 사진 삭제창 닫기"
          ></button>
          <div className="relative w-full max-w-sm rounded-2xl bg-background-50 p-6 shadow-2xl">
            <div className="flex flex-col items-center text-center">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <i className="ri-delete-bin-line text-xl text-red-500"></i>
              </div>
              <h3 className="text-base font-bold text-foreground-950">선택 사진 삭제</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-500">
                선택한 <strong className="text-foreground-800">{selectedIds.length}장</strong>을 휴지통으로 이동할까요?
              </p>
              <p className="mt-1 text-xs text-foreground-400">앨범에서 즉시 사라지며 원본과 썸네일이 함께 이동합니다.</p>
            </div>
            {batchDeleteError && (
              <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
                {batchDeleteError}
              </div>
            )}
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setShowBatchDeleteConfirm(false)}
                disabled={batchDeleting}
                className="flex-1 rounded-xl border border-background-200/70 px-4 py-3 text-sm font-medium text-foreground-600 hover:bg-secondary-50 disabled:opacity-50"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleBatchDelete}
                disabled={batchDeleting}
                className="flex-1 rounded-xl bg-red-500 px-4 py-3 text-sm font-bold text-white hover:bg-red-600 disabled:opacity-50"
              >
                {batchDeleting ? '삭제 중...' : `${selectedIds.length}장 삭제`}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Album Cover Picker */}
      {showCoverPicker && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center sm:px-6">
          <button
            type="button"
            className="absolute inset-0 bg-black/55 cursor-default"
            onClick={closeCoverPicker}
            aria-label="대표사진 선택창 닫기"
          ></button>
          <div className="relative w-full sm:max-w-lg max-h-[85vh] bg-background-50 rounded-t-3xl sm:rounded-2xl overflow-hidden shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70">
              <div>
                <h3 className="text-base font-bold text-foreground-950">대표사진 선택</h3>
                <p className="text-xs text-foreground-500 mt-0.5">이 앨범에 올라온 사진 중 한 장을 선택하세요.</p>
              </div>
              <button
                type="button"
                onClick={closeCoverPicker}
                disabled={savingCover}
                className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-secondary-100 transition-colors cursor-pointer disabled:opacity-50"
                aria-label="닫기"
              >
                <i className="ri-close-line text-xl text-foreground-600"></i>
              </button>
            </div>

            <div className="overflow-y-auto px-4 py-4">
              <div className="grid grid-cols-3 gap-2.5">
                {albumPhotos.map((photo) => {
                  const selected = selectedCoverUrl === photo.image;
                  return (
                    <button
                      type="button"
                      key={photo.id}
                      onClick={() => setSelectedCoverUrl(photo.image)}
                      className={`relative aspect-square rounded-xl overflow-hidden bg-secondary-100 cursor-pointer border-[3px] transition-all ${
                        selected
                          ? 'border-accent-500 shadow-[0_0_0_2px_rgba(157,121,78,0.18)]'
                          : 'border-transparent hover:border-background-300'
                      }`}
                      aria-label={`${photo.caption || photo.originalFilename} 대표사진으로 선택`}
                    >
                      <img
                        src={photo.image}
                        alt={photo.caption || photo.originalFilename}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      {selected && (
                        <div className="absolute inset-0 bg-black/15 flex items-start justify-end p-2">
                          <span className="w-6 h-6 rounded-full bg-accent-500 text-background-50 flex items-center justify-center shadow-md">
                            <i className="ri-check-line text-sm"></i>
                          </span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {coverError && (
                <div className="mt-4 px-3.5 py-3 rounded-xl bg-red-50 border border-red-200 text-xs text-red-600 flex items-center gap-2">
                  <i className="ri-error-warning-line text-base"></i>
                  {coverError}
                </div>
              )}
            </div>

            <div className="border-t border-background-200/70 px-4 py-4 flex gap-3 bg-background-50">
              <button
                type="button"
                onClick={closeCoverPicker}
                disabled={savingCover}
                className="flex-1 px-4 py-3 rounded-xl border border-background-200/70 text-sm font-medium text-foreground-600 hover:bg-secondary-50 transition-colors cursor-pointer disabled:opacity-50"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleSaveCover}
                disabled={!selectedCoverUrl || savingCover}
                className="flex-[1.4] px-4 py-3 rounded-xl bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingCover ? '저장 중...' : '대표사진으로 설정'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowDeleteConfirm(false)}
          ></div>
          <div className="relative bg-background-50 rounded-2xl w-full max-w-sm p-6">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-100 mb-4">
                <i className="ri-alert-line text-xl text-red-500"></i>
              </div>
              <h3 className="text-base font-bold text-foreground-950 mb-1">앨범 삭제</h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                정말 <strong className="text-foreground-800">"{album.title}"</strong> 앨범을 삭제할까요?<br />
                삭제된 앨범은 복구할 수 없어요.
              </p>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-3 rounded-xl border border-background-200/70 text-sm font-medium text-foreground-600 bg-background-50 hover:bg-secondary-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                취소
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-3 rounded-xl text-sm font-medium text-background-50 bg-red-500 hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {deleting ? '삭제 중...' : '삭제하기'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
